/**
 * Transaction duplicate-detection helpers shared by the bill editor.
 *
 * Mirrors the backend duplicate rule: a transaction matches another if they
 * share the transaction number OR the composite of date + time + location +
 * amount. Extracted from BillEditDialog so the matching logic is unit-testable
 * and reusable without pulling in React.
 */

const normTxn = (v) => (v === null || v === undefined ? "" : String(v).trim().toLowerCase());

/** Return the set of match keys (transaction-number key and/or composite key) for a txn. */
export function txnKeys(t) {
  const keys = [];
  const tn = normTxn(t.transaction_number);
  if (tn) keys.push("tn:" + tn);
  const raw = t.amount === "" || t.amount === null || t.amount === undefined
    ? null : parseFloat(String(t.amount).replace(/[$,]/g, ""));
  const amt = raw === null || isNaN(raw) ? null : raw;
  const d = normTxn(t.date), ti = normTxn(t.time), l = normTxn(t.location);
  if (d || ti || l || amt !== null) {
    keys.push("c:" + [d, ti, l, amt === null ? "" : amt.toFixed(2)].join("|"));
  }
  return keys;
}

/**
 * Live within-bill duplicate map: `{ rowIndex: indexOfFirstMatchingRow }`.
 * Updates as the user edits/removes rows.
 */
export function computeWithinDup(txns) {
  const seen = new Map();
  const dup = {};
  txns.forEach((t, i) => {
    const keys = txnKeys(t);
    let match = null;
    for (const k of keys) {
      if (seen.has(k)) { match = seen.get(k); break; }
    }
    if (match !== null && match !== undefined) dup[i] = match;
    keys.forEach((k) => { if (!seen.has(k)) seen.set(k, i); });
  });
  return dup;
}

/**
 * Cross-bill duplicate signatures (content-based, from the server review) —
 * robust to row edits. Returns a Set of match keys.
 */
export function computeCrossKeys(bill) {
  const set2 = new Set();
  const dups = bill?.review?.duplicate_transactions || [];
  const orig = bill?.transactions || [];
  dups.forEach((d) => {
    if (d.scope === "cross" || d.scope === "both") {
      const t = orig[d.index];
      if (t) txnKeys(t).forEach((k) => set2.add(k));
    }
  });
  return set2;
}

/** Human-readable flag label for a duplicate transaction row. */
export function dupRowLabel({ within, cross, dupOf }) {
  if (within && cross) return `Duplicate of row ${dupOf + 1} \u00b7 also in another bill`;
  if (within) return `Duplicate of row ${dupOf + 1}`;
  return "Also in another bill";
}
